/*
 * Licensed Materials - Property of IBM
 *
 * PID 5725-H26
 *
 * Copyright IBM Corporation 2023. All Rights Reserved.
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React, { Suspense, lazy } from 'react';
import { Switch, Redirect } from 'react-router-dom';
import { Authentication } from '@spm/universal-access';
import { Routes as UARoutes, TitledRoute, AppSpinner } from '@spm/universal-access-ui';
import { AuthenticatedRoute } from '@spm/core-ui';

/* import Loadable from 'react-loadable'; */
import PATHS from '@spm/universal-access-ui/src/router/Paths';
import applyForBenfitsTranslations from '@spm/universal-access-ui/src/features/ApplyForBenefits/components/ApplyForBenefitsComponentMessages';
import loginTranslations from '@spm/universal-access-ui/src/features/Login/components/LoginComponentMessages';
import routeTranslations from '@spm/universal-access-ui/src/router/RoutesMessages';
import routeTitles from './routesMessages';
import paths from './paths';
import Organization from './features/Organisation';
import signupTranslations from './features/Signup/components/SignupComponentMessages';
import applicationHeaderTranslations from './features/ApplicationHeader/components/ApplicationHeaderMessages';

/*  This routes.jsx file can be customized to add new components or
    Redirect the existing components to new routes:

   Sample:
     <Switch>
       <Route path="/ourPayments" component={OurPayments} />
       <Redirect from="/payments" to="/ourPayments" />
       <UARoutes />
     </Switch>
*/

/**
 * Default loading component to display while loading other components.
 */
const loadingComponent = () => <AppSpinner />;

/* BEGIN CC V803 UPGRADE : DS6.1.1 - react-loadable to React.Lazy () */

/* webpackChunkName: "SampleApplication" */
const SampleApplication = lazy(() =>
  import('./features/sampleApplication/SampleApplicationComponent')
);

/* webpackChunkName: "SampleApplicationForm" */
const SampleApplicationForm = lazy(() =>
  import('./features/sampleApplication/form/SampleApplicationFormComponent')
);

/* webpackChunkName: "SampleApplicationOverview" */
const SampleApplicationOverview = lazy(() =>
  import('./features/sampleApplication/overview/SampleApplicationOverviewComponent')
);

/* webpackChunkName: "SampleApplicationConfirmation" */
const SampleApplicationConfirmation = lazy(() =>
  import('./features/sampleApplication/confirmation/SampleApplicationConfirmation')
);

/* webpackChunkName: "PasswordResestForm" */
const PasswordResestForm = lazy(() => import('./features/PasswordReset'));

/* webpackChunkName: "ApplicationOverview" */
const ApplicationOverview = lazy(() => import('./features/ApplicationOverview'));

/* webpackChunkName: "SubmissionFormConfirmation" */
const SubmissionFormConfirmation = lazy(() =>
  import('./features/Forms/SubmissionForm/SubmissionFormConfirmationComponent')
);

/* webpackChunkName: "Signup" */
const Signup = lazy(() => import('./features/Signup'));

/* webpackChunkName: "Profile" */
const Profile = lazy(() => import('./features/Profile'));

/* webpackChunkName: "AccountHome" */
const AccountHome = lazy(() => import('./features/AccountHome'));

/* webpackChunkName: "ApplyForBenefits" */
const ApplyForBenefits = lazy(() => import('./features/ApplyForBenefits'));

/* webpackChunkName: "Verifications" */
const Verifications = lazy(() => import('./features/Verifications'));

/* webpackChunkName: "VerificationDetail" */
const VerificationDetail = lazy(() => import('./features/VerificationDetail'));

/* webpackChunkName: "YourBenefits" */
const YourBenefits = lazy(() => import('./features/YourBenefits'));

/* webpackChunkName: "ContactUs" */
const ContactUs = lazy(() => import('./features/ContactUs'));

/* webpackChunkName: "Disclaimer" */
const Disclaimer = lazy(() => import('./features/Disclaimer'));

/* webpackChunkName: "Privacy" */
const Privacy = lazy(() => import('./features/PrivacyNotice'));

/* webpackChunkName: "Login" */
const Login = lazy(() => import('./features/Login'));

/* webpackChunkName: "SubmissionForm" */
const SubmissionForm = lazy(() => import('./features/Forms/SubmissionForm'));

/* webpackChunkName: "ActivateCitizenAccount" */
const ActivateCitizenAccount = lazy(() => import('./features/ActivateCitizenAccount'));

/* webpackChunkName: "ActivateEmail" */
const ActivateEmail = lazy(() => import('./features/ActivateEmail'));

/* webpackChunkName: "EmailPasswordReset" */
const EmailPasswordReset = lazy(() => import('./features/EmailPasswordReset'));

/* webpackChunkName: "ForgotPasswordReset" */
const ForgotPasswordReset = lazy(() => import('./features/ForgotPasswordReset'));

/* webpackChunkName: "ForgotUserName" */
const ForgotUserName = lazy(() => import('./features/ForgotUserName'));

/* webpackChunkName: "AccountUnlock" */
const AccountUnlock = lazy(() => import('./features/AccountUnlock'));

/* webpackChunkName: "AccountUnlockEmail" */
const AccountUnlockEmail = lazy(() => import('./features/AccountUnlockEmail'));

/* webpackChunkName: "EnrollWaitlist" */
const EnrollWaitlist = lazy(() =>
  import('./features/EnrollWaitlist/components/EnrollWaitlistComponent')
);
/* END CC V803 UPGRADE : DS6.1.1 - react-loadable to React.Lazy () */

/* START CHAP3 : Application overview for CHAP3 */
const CHAP3ApplicationOverview = lazy(() =>
  import('./features/CHAP3ApplicationOverview')
);
/* END CHAP3 : Application overview for CHAP3 */

/* webpackChunkName: "ApplicationForm" */
const ApplicationForm = lazy(() => import('./features/Forms/ApplicationForm'));

export default (
  <>
    <Suspense fallback={loadingComponent}>
      <Switch>
        {/* Sample Routes - Example features. Not intended to run in production. */}
        <TitledRoute
          component={Organization}
          exact
          path={paths.HOME}
          title={applicationHeaderTranslations.headerLandingPageTitle}
        />
        <TitledRoute
          component={SampleApplicationForm}
          exact
          path={`${paths.SAMPLE_APPLICATION.FORM}/:formId`}
          title={routeTitles.sampleApplicationForm}
        />
        <TitledRoute
          component={SampleApplicationConfirmation}
          exact
          path={`${paths.SAMPLE_APPLICATION.CONFIRMATION}/:formId`}
          title={routeTitles.sampleApplicationConfirmation}
        />
        <TitledRoute
          component={SampleApplicationOverview}
          exact
          path={paths.SAMPLE_APPLICATION.OVERVIEW}
          title={routeTitles.sampleApplicationOverview}
        />
        <TitledRoute
          component={SampleApplication}
          exact
          path={paths.SAMPLE_APPLICATION.ROOT}
          title={routeTitles.sampleApplication}
        />
        <TitledRoute
          component={PasswordResestForm}
          exact
          path={`${paths.PASSWORD_RESET.FORM}/:hashToken`}
          title={routeTitles.passwordReset}
        />
        <TitledRoute
          component={ActivateCitizenAccount}
          exact
          path={`${paths.ACTIVATE_CITIZEN_ACCOUNT}/:hashToken`}
          title={routeTitles.activateCitizenAccount}
        />
        <TitledRoute
          component={ActivateEmail}
          exact
          path={`${paths.ACTIVATE_EMAIL}/:hashToken`}
          title={routeTitles.activateEmail}
        />
        <TitledRoute
          component={AccountUnlock}
          exact
          path={`${paths.ACCOUNT_UNLOCK}/:hashToken`}
          title={routeTitles.accountUnlock}
        />
        <TitledRoute
          component={AccountUnlockEmail}
          exact
          path={paths.ACCOUNT_UNLOCK_EMAIL}
          title={routeTitles.accountUnlockEmail}
        />
        <TitledRoute
          component={ForgotUserName}
          exact
          path={paths.FORGOT_USERNAME}
          title={routeTitles.forgotUserName}
        />
        <TitledRoute
          component={EmailPasswordReset}
          exact
          path={paths.EMAIL_PASSWORD_RESET}
          title={routeTitles.emailPasswordReset}
        />
        <TitledRoute
          component={ForgotPasswordReset}
          exact
          path={`${paths.FORGOT_PASSWORD_RESET}/:hashToken`}
          title={routeTitles.forgotPasswordReset}
        />
        <TitledRoute
          component={ApplicationOverview}
          exact
          path={paths.CHAP_APPLICATION.APPLICATION_OVERVIEW}
          title={routeTitles.chapApplication}
        />
        <TitledRoute
          component={SubmissionFormConfirmation}
          exact
          path={`${paths.APPLY}/:applicationFormId/success`}
          title={routeTitles.submissionFormConfirmation}
        />
        <TitledRoute
          component={Signup}
          exact
          path={PATHS.SIGNUP}
          title={signupTranslations.title}
        />
        <AuthenticatedRoute
          authUserTypes={[Authentication.USER_TYPES.STANDARD, Authentication.USER_TYPES.LINKED]}
          component={Profile}
          exact
          path={PATHS.PROFILE}
          title={applicationHeaderTranslations.headerProfileText}
        />
        <AuthenticatedRoute
          authUserTypes={[Authentication.USER_TYPES.STANDARD, Authentication.USER_TYPES.LINKED]}
          component={AccountHome}
          exact
          path={PATHS.ACCOUNT}
          title={applicationHeaderTranslations.headerDashboardLabel}
        />
        <TitledRoute
          component={ApplyForBenefits}
          exact
          path={PATHS.APPLY}
          title={applyForBenfitsTranslations.applyForBenefitsPageTitle}
        />
        <Redirect path={PATHS.PASSWORD_CHANGED} to={PATHS.ACCOUNT} />
        <AuthenticatedRoute
          authUserTypes={[Authentication.USER_TYPES.LINKED, Authentication.USER_TYPES.STANDARD]}
          component={Verifications}
          exact
          path={paths.VERIFICATIONS.ROOT}
          title={applicationHeaderTranslations.headerYourVerificationsText}
        />
        <AuthenticatedRoute
          authUserTypes={[Authentication.USER_TYPES.LINKED, Authentication.USER_TYPES.STANDARD]}
          component={VerificationDetail}
          exact
          path={paths.VERIFICATIONS.DETAILS}
          title={routeTitles.verificationDetails}
        />
        <AuthenticatedRoute
          authUserTypes={[Authentication.USER_TYPES.STANDARD, Authentication.USER_TYPES.LINKED]}
          component={YourBenefits}
          exact
          path={PATHS.BENEFITS}
          title={applicationHeaderTranslations.headerYourBenefitsLabel}
        />
        <TitledRoute
          component={ContactUs}
          exact
          path={PATHS.CONTACT_US}
          title={applicationHeaderTranslations.headerContactUsLabel}
        />
        <TitledRoute
          component={Disclaimer}
          exact
          path={paths.DISCLAIMER}
          title={routeTitles.footerDisclaimerLabel}
        />
        <TitledRoute
          component={Privacy}
          exact
          path={paths.PRIVACY}
          title={routeTitles.footerPrivacyLabel}
        />
        <TitledRoute component={Login} exact path={PATHS.LOGIN} title={loginTranslations.title} />
        <TitledRoute
          component={SubmissionForm}
          exact
          isIEG
          path={`${PATHS.APPLY}/:submissionFormId/submit`}
          title={routeTranslations.submissionForm}
        />
        <TitledRoute
          component={SubmissionForm}
          exact
          isIEG
          path={`${PATHS.APPLY}/new/:applicationFormId/submit`}
          title={routeTranslations.submissionForm}
        />
        <AuthenticatedRoute
          authUserTypes={[Authentication.USER_TYPES.STANDARD, Authentication.USER_TYPES.LINKED]}
          component={EnrollWaitlist}
          exact
          path={`${paths.ENROLL_WAITLIST.FORM}/:formId`}
          title={routeTitles.enrollWaitlist}
        />
        <TitledRoute
          component={CHAP3ApplicationOverview}
          exact
          path={paths.CHAP3_APPLICATION.APPLICATION_OVERVIEW}
          title={routeTitles.chapApplication}
        />
        <TitledRoute
            component={ApplicationForm}
            exact
            isIEG
            path={`${PATHS.APPLY}/new/:applicationTypeId`}
            title={routeTranslations.applicationForm}
          />
          <TitledRoute
            component={ApplicationForm}
            exact
            isIEG
            path={`${PATHS.APPLY}/:applicationFormId`}
            title={routeTranslations.applicationForm}
          />
        {/* UA Routes */}
        <UARoutes />
      </Switch>
    </Suspense>
  </>
);